import React, { useEffect, useRef, useState } from "react";
import { Shield, Scale, FileCheck2, Sparkles, Check, FileText, Download } from "lucide-react";

/**
 * Hero3DPreview — interactive 3D product preview for the landing hero.
 *
 * Features:
 *   - Mouse-tracked 3D tilt (CSS perspective + rotateX/rotateY)
 *   - Cursor-following light glow inside the card
 *   - Sequential row reveal animation on mount
 *   - Typewriter effect on the lawyer prompt (loops every ~12s)
 *   - Secondary floating "PDF export" card peeking from behind (depth)
 *   - Respects prefers-reduced-motion
 *
 * Pure presentational component — no external assets, no network calls.
 */
export default function Hero3DPreview({ lang = "el" }) {
  const wrapperRef = useRef(null);
  const cardRef = useRef(null);
  const glowRef = useRef(null);

  const lawyerPrompt = lang === "el"
    ? "Πελάτης απολύθηκε προφορικά μετά από 4 χρόνια εργασίας, χωρίς προειδοποίηση. Δικαιούται αποζημίωση κατά τον Κεφ. 24;"
    : "Client was orally dismissed after 4 years of employment without notice. Is compensation due under Cap. 24?";

  const [typedPrompt, setTypedPrompt] = useState("");
  const [showRows, setShowRows] = useState(false);
  const reducedMotion = typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  // Typewriter effect + sequential row reveal, loops every ~12s
  useEffect(() => {
    if (reducedMotion) {
      setTypedPrompt(lawyerPrompt);
      setShowRows(true);
      return;
    }
    let timeouts = [];
    let intervalId = null;

    const runCycle = () => {
      setTypedPrompt("");
      setShowRows(false);
      let i = 0;
      // Type prompt char-by-char
      const typeStep = () => {
        if (i <= lawyerPrompt.length) {
          setTypedPrompt(lawyerPrompt.slice(0, i));
          i += 1;
          timeouts.push(setTimeout(typeStep, 28));
        } else {
          // Reveal rows after short pause
          timeouts.push(setTimeout(() => setShowRows(true), 350));
        }
      };
      typeStep();
    };

    runCycle();
    intervalId = setInterval(runCycle, 14000);

    return () => {
      timeouts.forEach(clearTimeout);
      if (intervalId) clearInterval(intervalId);
    };
  }, [lawyerPrompt, reducedMotion]);

  // Mouse-tracked 3D tilt
  useEffect(() => {
    if (reducedMotion) return;
    const wrapper = wrapperRef.current;
    const card = cardRef.current;
    const glow = glowRef.current;
    if (!wrapper || !card) return;

    let raf = null;
    const onMove = (e) => {
      const rect = wrapper.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      // Range: -1 to 1 around center
      const dx = (x - 0.5) * 2;
      const dy = (y - 0.5) * 2;
      // Tilt limits (degrees)
      const rotY = dx * 6;
      const rotX = -dy * 5;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        card.style.transform = `perspective(1400px) rotateX(${rotX}deg) rotateY(${rotY}deg) translateZ(0)`;
        if (glow) {
          glow.style.background =
            `radial-gradient(420px circle at ${x * 100}% ${y * 100}%, rgba(139,30,63,0.10), transparent 55%)`;
        }
      });
    };
    const onLeave = () => {
      if (raf) cancelAnimationFrame(raf);
      card.style.transform = "perspective(1400px) rotateX(0deg) rotateY(0deg)";
      if (glow) glow.style.background = "transparent";
    };

    wrapper.addEventListener("mousemove", onMove);
    wrapper.addEventListener("mouseleave", onLeave);
    return () => {
      wrapper.removeEventListener("mousemove", onMove);
      wrapper.removeEventListener("mouseleave", onLeave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reducedMotion]);

  const rows = [
    { n: "01", k: lang === "el" ? "Σύντομη απάντηση" : "Quick answer",        v: lang === "el" ? "Ναι. Δικαιούται αποζημίωση." : "Yes. Compensation is due.", accent: true },
    { n: "04", k: lang === "el" ? "Εφαρμοστέο δίκαιο" : "Applicable law",       v: lang === "el" ? "Κεφ. 24, άρθρο 5(1)" : "Cap. 24, section 5(1)" },
    { n: "05", k: lang === "el" ? "Νομολογία" : "Case law",                     v: lang === "el" ? "Παπαδόπουλος ν. CCC, 2018" : "Papadopoulos v. CCC, 2018" },
    { n: "08", k: lang === "el" ? "Κρίσιμη προθεσμία" : "Critical deadline",    v: lang === "el" ? "12 μήνες παραγραφής" : "12-month limitation" },
  ];

  return (
    <div ref={wrapperRef} className="relative" style={{ perspective: "1400px" }} data-testid="hero-product-preview">
      {/* Soft glow background */}
      <div className="absolute -inset-8 bg-gradient-to-br from-[#F8DBE4]/70 via-transparent to-[#D8E2DC]/60 rounded-[36px] blur-3xl pointer-events-none" />

      {/* Secondary floating card (depth, peeks from behind, tilted) */}
      <div
        aria-hidden="true"
        className="absolute -right-6 sm:-right-10 top-12 w-40 sm:w-52 bg-white rounded-2xl border border-slate-200 shadow-[0_20px_50px_rgba(10,25,47,0.10)] p-3 pointer-events-none drift-slower"
        style={{ transform: "rotate(6deg) translateZ(0)", zIndex: 1 }}
      >
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-[#FBE9EE] text-[#8B1E3F] flex items-center justify-center">
            <FileText className="h-3.5 w-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono text-slate-400">A-2045</div>
            <div className="text-[11px] font-bold text-[#0A192F] truncate">{lang === "el" ? "Αναφορά.pdf" : "Report.pdf"}</div>
          </div>
        </div>
        <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full w-3/4 bg-gradient-to-r from-[#0A192F] to-[#8B1E3F] rounded-full" />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[10px]">
          <span className="text-slate-500">11 / 11</span>
          <span className="inline-flex items-center gap-1 font-mono text-[#8B1E3F] font-bold">
            <Download className="h-2.5 w-2.5" /> 1.4s
          </span>
        </div>
      </div>

      {/* Main browser-chrome card */}
      <div
        ref={cardRef}
        className="relative rounded-2xl bg-white shadow-[0_30px_80px_rgba(10,25,47,0.22)] border border-slate-200 overflow-hidden"
        style={{
          transformStyle: "preserve-3d",
          transition: "transform .35s cubic-bezier(0.16, 1, 0.3, 1)",
          zIndex: 2,
        }}
      >
        {/* Cursor-following highlight glow inside card */}
        <div
          ref={glowRef}
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none transition-[background] duration-200"
        />

        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200 relative">
          <span className="h-3 w-3 rounded-full bg-[#FF5F57]" />
          <span className="h-3 w-3 rounded-full bg-[#FEBC2E]" />
          <span className="h-3 w-3 rounded-full bg-[#28C840]" />
          <div className="ml-4 flex-1 flex items-center justify-center">
            <div className="px-3 py-1 rounded-md bg-white border border-slate-200 text-[11px] font-mono text-slate-500 inline-flex items-center gap-1.5">
              <Shield className="h-3 w-3 text-emerald-500" />
              dikigoros.io/matter
            </div>
          </div>
        </div>

        {/* User prompt with typewriter */}
        <div className="px-6 pt-5 pb-3 bg-gradient-to-b from-slate-50/60 to-white relative">
          <div className="flex items-start gap-3">
            <div className="h-7 w-7 rounded-full bg-[#0A192F] text-white flex items-center justify-center text-[11px] font-bold flex-none">
              ΑΚ
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-mono text-slate-500 mb-1">{lang === "el" ? "Δικηγόρος" : "Lawyer"}</div>
              <div className="text-sm text-slate-800 leading-relaxed bg-slate-50 rounded-xl rounded-tl-sm px-4 py-2.5 border border-slate-200 min-h-[64px]">
                {typedPrompt}
                {typedPrompt.length < lawyerPrompt.length && !reducedMotion && (
                  <span className="inline-block w-[2px] h-[14px] bg-[#8B1E3F] ml-0.5 align-middle animate-pulse" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Assistant response */}
        <div className={`px-6 pb-6 pt-2 transition-opacity duration-500 ${showRows ? "opacity-100" : "opacity-0"}`}>
          <div className="flex items-start gap-3">
            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#8B1E3F] to-[#6F1530] text-white flex items-center justify-center flex-none shadow-md shadow-[#8B1E3F]/30">
              <Scale className="h-3.5 w-3.5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-[11px] font-mono text-slate-500">Dikigoros.io</div>
                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {lang === "el" ? "Υψηλή βεβαιότητα · 94%" : "High confidence · 94%"}
                </span>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                {rows.map((row, idx) => (
                  <div
                    key={row.n}
                    className={`flex items-center gap-3 px-3.5 py-2.5 transition-all duration-500 ${idx > 0 ? "border-t border-slate-100" : ""} ${row.accent ? "bg-[#FBE9EE]/40" : ""} ${showRows ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"}`}
                    style={{ transitionDelay: showRows ? `${idx * 110}ms` : "0ms" }}
                  >
                    <span className="font-mono text-[10px] tracking-wider font-bold text-[#8B1E3F] flex-none">{row.n}</span>
                    <span className="text-[12px] font-semibold text-slate-700 flex-none w-[120px]">{row.k}</span>
                    <span className={`text-[12px] truncate ${row.accent ? "font-semibold text-[#0A192F]" : "text-slate-600"}`}>{row.v}</span>
                    <Check className="h-3.5 w-3.5 text-emerald-500 ml-auto flex-none" />
                  </div>
                ))}
                <div className="px-3.5 py-2 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 flex items-center justify-between">
                  <span>{lang === "el" ? "+7 ενότητες ακόμα" : "+7 more sections"}</span>
                  <span className="font-mono text-[#8B1E3F]">{lang === "el" ? "Έτοιμη · 1.4s" : "Ready · 1.4s"}</span>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2">
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-600 inline-flex items-center gap-1.5">
                  <FileCheck2 className="h-3 w-3" /> {lang === "el" ? "Εξαγωγή PDF" : "Export PDF"}
                </span>
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-600 inline-flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3" /> {lang === "el" ? "Διερεύνηση" : "Deep dive"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating "LIVE DEMO" pill */}
      <div
        className="absolute -top-3 right-6 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0A192F] text-white text-[10px] font-bold tracking-wider shadow-lg shadow-[#0A192F]/30"
        style={{ zIndex: 3 }}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
        {lang === "el" ? "ΖΩΝΤΑΝΟ ΔΕΙΓΜΑ" : "LIVE DEMO"}
      </div>
    </div>
  );
}
